import { useCallback, useMemo } from 'react';
import type { Query, QueryKey } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import type {
  IxmUserauthV1JoinedRealmAccess,
  IxmUserauthV1RealmAccessTokenServiceGetRealmAccessTokenResponse,
  IxmUserauthV1TenantState,
} from '@interxfi/types/generated/api';
import { useRealmAuthActiveAccess, useRealmAuthContext, type useRealmAuthStore } from './context';
import { useIxmUserauthV1RealmAccessTokenServiceGetRealmAccessToken } from './generated/hooks/ixm-userauth-v1-realm-access-token-service-get-realm-access-token';

export enum TenantContext {
  Program = 'program',
  Clearing = 'clearing',
  O5 = 'o5',
  Platform = 'platform',
  Organization = 'organization',
}

function getBaseUrl(realm: IxmUserauthV1JoinedRealmAccess | undefined) {
  // rewrite the baseUrl in dev to allow requests to be proxied to the dev api
  if (!(import.meta as any).env.VITE_MOCK && (process as any).env.NODE_ENV === 'development') {
    if (realm?.realm?.data.spec?.baseUrl) {
      const url = new URL(realm.realm.data.spec.baseUrl);
      return `${window.location.origin}/proxy-${url.host.split('.')[0]}`;
    }
  }

  return realm?.realm?.data.spec?.baseUrl;
}

function getRealmAccessContextExtension(activeRealmAccess: IxmUserauthV1JoinedRealmAccess | undefined) {
  if (!activeRealmAccess?.access) {
    return {};
  }

  const context: Record<string, string> = {
    realmAccessStatus: activeRealmAccess.access.status,
    isAdmin: Boolean(activeRealmAccess.access.data.spec?.isAdmin).toString(),
  };

  if (activeRealmAccess.realm?.data.spec?.baseUrl) {
    context.baseUrl = activeRealmAccess.realm.data.spec.baseUrl;
  }

  if (activeRealmAccess.access.realmAccessId) {
    context.realmAccessId = activeRealmAccess.access.realmAccessId;
  }

  return context;
}

function getProgramIdFromRealmAccess(realmAccess: IxmUserauthV1JoinedRealmAccess | undefined) {
  if (realmAccess?.tenant?.tenantType === TenantContext.Program) {
    return realmAccess.tenant.tenantId;
  }

  return undefined;
}

function getOrganizationIdFromRealmAccess(realmAccess: IxmUserauthV1JoinedRealmAccess | undefined) {
  if (realmAccess?.tenant?.tenantType === TenantContext.Organization) {
    return realmAccess.tenant.tenantId;
  }

  return undefined;
}

function tokenRefreshIntervalFn(
  query: Query<
    IxmUserauthV1RealmAccessTokenServiceGetRealmAccessTokenResponse | undefined,
    Error,
    IxmUserauthV1RealmAccessTokenServiceGetRealmAccessTokenResponse | undefined,
    QueryKey
  >,
): number | false {
  if (query.state.data?.token) {
    const decodedToken = jwtDecode(query.state.data.token);
    const expiresIn = (decodedToken.exp || 0) * 1_000 - Date.now();

    return Math.max(0, expiresIn - 300_000); // refresh 5 minutes before token expires
  }

  return false;
}

/**
 * Returns a boolean when the scope action is query.
 */
function getIsReadOnly(access: IxmUserauthV1JoinedRealmAccess | undefined): boolean | undefined {
  if (!access?.access) {
    return undefined;
  }

  const scopes = access.role?.data.spec?.scopes || [];

  // Currently frontend scopes are naive and only contain `*` -- for all write access
  // -- or `*/query` -- for read-only access. In some future, scopes will be expanded
  // to allow for granular access scopes.
  return scopes.every((scope) => scope.split('/').at(-1) === 'query');
}

function getTenantContext(tenant: IxmUserauthV1TenantState | undefined): TenantContext | undefined {
  if (!tenant?.tenantType) {
    return undefined;
  }

  if (
    Object.values(TenantContext)
      .map((x) => x.toLowerCase())
      .includes(tenant.tenantType)
  ) {
    return tenant.tenantType as TenantContext;
  }

  return undefined;
}

const emptyCtx = {};

export function useRealmAPIContext(access: IxmUserauthV1JoinedRealmAccess | undefined) {
  const ctx = useRealmAuthContext();
  const { data, error, isPending } = useIxmUserauthV1RealmAccessTokenServiceGetRealmAccessToken(
    access?.access?.realmAccessId ? { realmAccessId: access.access.realmAccessId } : undefined,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchIntervalInBackground: true,
      refetchInterval: tokenRefreshIntervalFn,
    },
  );

  const realmContextExtension = useMemo(() => {
    // Ensure there's a token for the context extension
    if (!data?.token) {
      return emptyCtx;
    }

    return getRealmAccessContextExtension(access);
  }, [access, data?.token]);
  const extendKey = useCallback(<T extends readonly any[]>(key: T) => [...key, realmContextExtension] as const, [realmContextExtension]);
  const tenantContext = useMemo(() => getTenantContext(access?.tenant), [access?.tenant]);
  const programId = useMemo(() => getProgramIdFromRealmAccess(access), [access]);
  const organizationId = useMemo(() => getOrganizationIdFromRealmAccess(access), [access]);
  const realmIsSimulated = access?.realm?.data?.spec?.metadata?.connectivity === 'simulated';
  const realmIsSand = access?.realm?.realmCode === 'sand';
  const scopes = access?.role?.data.spec?.scopes;
  const isReadOnly = useMemo(() => getIsReadOnly(access), [access]);
  const tenantOrganizationMembership = organizationId
    ? ctx?.organizationMemberships?.find((membership) => membership.organization?.organizationId === organizationId)
    : undefined;

  return useMemo(
    () => ({
      realm: access?.realm,
      tenant: access?.tenant,
      tenantContext,
      organizationId,
      programId,
      access: access?.access,
      baseUrl: getBaseUrl(access),
      token: data?.token,
      scopes,
      isReadOnly,
      extendKey,
      error,
      isPending,
      tenantIsClearing: Boolean(tenantContext === TenantContext.Clearing),
      tenantIsPlatform: Boolean(tenantContext === TenantContext.Platform),
      tenantIsOrganization: Boolean(tenantContext === TenantContext.Organization),
      tenantIsProgram: Boolean(tenantContext === TenantContext.Program),
      tenantOrganizationMembership,
      realmIsSimulated,
      realmIsSand,
    }),
    [
      organizationId,
      programId,
      access,
      tenantContext,
      realmIsSimulated,
      realmIsSand,
      tenantOrganizationMembership,
      data?.token,
      scopes,
      isReadOnly,
      error,
      isPending,
      extendKey,
    ],
  );
}

export function useActiveRealmAPIContext() {
  const activeRealmAccess = useRealmAuthActiveAccess();
  const {
    realm,
    tenant,
    tenantContext,
    organizationId,
    programId,
    access,
    baseUrl,
    token,
    scopes,
    isReadOnly,
    extendKey,
    error,
    isPending,
    realmIsSimulated,
    realmIsSand,
    tenantIsClearing,
    tenantIsOrganization,
    tenantIsPlatform,
    tenantIsProgram,
    tenantOrganizationMembership,
  } = useRealmAPIContext(activeRealmAccess);

  return useMemo(
    () => ({
      activeRealmAccess,
      activeRealm: realm,
      activeTenant: tenant,
      activeTenantContext: tenantContext,
      activeOrganizationId: organizationId,
      activeOrganizationMembership: tenantOrganizationMembership,
      activeProgramId: programId,
      activeAccess: access,
      baseUrl,
      token,
      activeScopes: scopes,
      activeIsReadOnly: isReadOnly,
      extendKey,
      error,
      isPending,
      activeTenantIsClearing: tenantIsClearing,
      activeTenantIsPlatform: tenantIsPlatform,
      activeTenantIsProgram: tenantIsProgram,
      activeTenantIsOrganization: tenantIsOrganization,
      activeRealmIsSimulated: realmIsSimulated,
      activeRealmIsSand: realmIsSand,
    }),
    [
      activeRealmAccess,
      realm,
      tenant,
      tenantContext,
      organizationId,
      programId,
      access,
      baseUrl,
      token,
      scopes,
      isReadOnly,
      extendKey,
      error,
      isPending,
      realmIsSimulated,
      realmIsSand,
      tenantIsClearing,
      tenantIsPlatform,
      tenantIsOrganization,
      tenantIsProgram,
      tenantOrganizationMembership,
    ],
  );
}

export type ActiveRealmContextType = ReturnType<typeof useActiveRealmAPIContext>;

export type RealmAuthStore = ReturnType<typeof useRealmAuthStore.getState>;
